import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Settings, ArrowLeft, Store, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success('Giriş başarılı!');
      // Satıcı ise seller dashboard'a yönlendir
      if (userData.user_type === 'seller' || userData.role === 'seller' || userData.is_seller) {
        navigate('/seller/dashboard', { replace: true });
      } else if (userData.user_type === 'admin' || userData.role === 'admin') {
        navigate('/sistem-yonetimi', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-8">
            <ArrowLeft className="h-4 w-4" />
            Ana Sayfaya Dön
          </Link>

          <Card>
            <CardHeader className="text-center">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Settings className="h-7 w-7 text-white" />
              </div>
              <CardTitle className="text-2xl">Hoş Geldiniz</CardTitle>
              <CardDescription>Hesabınıza giriş yapın</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">E-posta</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Şifre</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" className="rounded" />
                    Beni hatırla
                  </label>
                  <Link to="/forgot-password" className="text-sm text-orange-500 hover:text-orange-600">
                    Şifremi unuttum
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  disabled={loading}
                  data-testid="login-btn"
                >
                  {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                </Button>
              </form>

              {/* Misafir Alışveriş Butonu */}
              <div className="mt-4 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/guest-checkout')}
                  className="w-full border-orange-500 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                  data-testid="guest-shopping-btn"
                >
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Misafir Olarak Alışveriş Yap
                </Button>
                <p className="text-xs text-center text-slate-400 mt-2">
                  Kayıt olmadan hızlıca alışveriş yapın
                </p>
              </div>

              <div className="mt-6 text-center text-sm text-slate-500">
                Hesabınız yok mu?{' '}
                <Link to="/register" className="text-orange-500 hover:text-orange-600 font-medium">
                  Kayıt Ol
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-slate-900 to-slate-800 items-center justify-center p-8">
        <div className="text-center text-white max-w-md">
          <div className="w-20 h-20 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <Store className="h-10 w-10" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Pazarlo</h2>
          <p className="text-slate-300 text-lg">
            Türkiye'nin en büyük oto yedek parça pazarına hoş geldiniz.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
